import React, { ComponentProps } from "react";
import { useLocation } from "react-router-dom";
import { useSignalR } from "sonobello.utilities.react.signalr";

import AppContextValueProvider from "../../App/compositions/AppContextValueProvider";
import CalendarSignalRListener, {
  ICalendarSignalRListenerConfig
} from "../../Events/Consumers/Components/CalendarSignalRListener";
import { ISignalRConnectionConfig } from "../../Events/Consumers/Components/SignalRConnection";
import CachedSignalRConnection from "../../Events/Consumers/compositions/CachedSignalRConnection";
import usePostSignalRNegotiate from "../../Events/Consumers/hooks/usePostSignalRNegotiate";
import { ICalendarEvent } from "../../Events/Consumers/types/ICalendarEvent";
import IPathObserver from "../../routing/types/IPathObserver";
import CalendarsManager, { ICalendarWorkerProps, ICalendarsManagerProps } from "../Components/CalendarManager";
import CenterCalendarWorker from "../Components/CenterCalendarWorker";
import ICalendar from "../types/ICalendar";
import AppContextCalendarController from "./AppContextCalendarController";
import AppContextUseGetBookingCenterOptions from "./AppContextUseGetBookingCenterOptions";
import AppContextUseGetCenterCalendar from "./AppContextUseGetCenterCalendar";

const AppContextCalendarManager = AppContextValueProvider<
  ICalendarsManagerProps,
  ComponentProps<typeof CalendarsManager>
>({
  Component: CalendarsManager,
  map: ({ patient, defaultCenter, sessionKeyCState, router, setBookingController }) => {
    return {
      businessUnitId: patient.lead.campaign.businessUnit.id,
      patientFirstName: patient.firstName,
      defaultCenter,
      router,
      sessionKey: sessionKeyCState,
      setBookingController
    };
  }
});

const Worker: React.FC<ICalendarWorkerProps<ICalendarEvent, ICalendar>> = props => {
  const location = useLocation();
  return (
    <AppContextCalendarController pathName={location.pathname}>
      <CenterCalendarWorker {...props} useGetCenterCalendar={AppContextUseGetCenterCalendar} />
    </AppContextCalendarController>
  );
};

const Manager: ICalendarSignalRListenerConfig["CalendarManager"] = props => (
  <AppContextCalendarManager {...props} useGetCenters={AppContextUseGetBookingCenterOptions} Worker={Worker} />
);

const SignalRListener: ISignalRConnectionConfig["SignalRListener"] = props => (
  <CalendarSignalRListener {...props} CalendarManager={Manager} />
);

/** A fully configured calendar manager which uses OPS. */
const CalendarManager: React.FC<
  Pick<ISignalRConnectionConfig, "router"> & Pick<ICalendarsManagerProps, "router"> & IPathObserver
> = ({ router }) => {
  return (
    <CachedSignalRConnection
      router={router}
      useGetConnectionInfo={usePostSignalRNegotiate}
      useSignalR={useSignalR}
      maxConnectionAttempts={3}
      SignalRListener={SignalRListener}
    />
  );
};

export default CalendarManager;
